"use client"

import * as React from "react"
import { cn } from "../../lib/utils"
import { UIBox } from "../layout/ui-box"
import { UITypography } from "./ui-typography"
import { Badge } from "../../components/badge"

export interface UIDateTimeCellProps {
  /** Date value (ISO string or Date object) */
  date: string | Date | null | undefined
  /** Locale for formatting (typically from useLocale()) */
  locale?: string
  /** Show relative time badge (e.g., "3 days ago"). Defaults to true */
  showRelative?: boolean
  /** Fallback text when date is invalid/null */
  fallback?: string
  /** Extra classes on the outer UIBox */
  className?: string
  /** Additional classes for absolute datetime text */
  textClassName?: string
}

function formatRelative(date: Date, locale?: string): string {
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" })
  if (minutes < 1) return rtf.format(0, "second")
  if (minutes < 60) return rtf.format(-minutes, "minute")
  if (hours < 24) return rtf.format(-hours, "hour")
  if (days < 30) return rtf.format(-days, "day")
  const months = Math.floor(days / 30)
  if (months < 12) return rtf.format(-months, "month")
  return rtf.format(-Math.floor(months / 12), "year")
}

/**
 * 리스트/테이블 셀에서 생성일/수정일 등 타임스탬프를 통일된 스타일로 렌더링.
 *
 * 표준 패턴: `UITypography variant="muted" text-xs` + `toLocaleString(locale)` +
 * optional `UIBadge variant="outline"` with relative time.
 *
 * @example
 *   <UIDateTimeCell date={row.created_at} locale={locale} />
 *   <UIDateTimeCell date={row.updated_at} locale={locale} showRelative={false} />
 */
export const UIDateTimeCell = React.memo(function UIDateTimeCell({
  date,
  locale,
  showRelative = true,
  fallback = "-",
  className,
  textClassName,
}: UIDateTimeCellProps) {
  if (!date) {
    return (
      <UITypography as="span" variant="muted" className={cn("text-xs", textClassName)}>
        {fallback}
      </UITypography>
    )
  }

  const d = typeof date === "string" ? new Date(date) : date
  if (isNaN(d.getTime())) {
    return (
      <UITypography as="span" variant="muted" className={cn("text-xs", textClassName)}>
        {fallback}
      </UITypography>
    )
  }

  return (
    <UIBox className={cn("flex items-center gap-2 min-w-0", className)}>
      <UITypography
        as="span"
        variant="muted"
        className={cn("text-xs truncate min-w-0", textClassName)}
      >
        {d.toLocaleString(locale)}
      </UITypography>
      {showRelative && (
        <Badge variant="outline" className="text-xs flex-shrink-0">
          {formatRelative(d, locale)}
        </Badge>
      )}
    </UIBox>
  )
})
